import React, { useState, useEffect, useMemo, useRef } from "react";

export const SyringeVisualization = ({ fillPercentage, totalUnits, syringeVolume }) => {
  const [animatedFill, setAnimatedFill] = useState(fillPercentage);
  const prevFillRef = useRef(fillPercentage);
  
  useEffect(() => {
    if (prevFillRef.current !== fillPercentage) {
      prevFillRef.current = fillPercentage;
      const animationFrame = requestAnimationFrame(() => {
        setAnimatedFill(fillPercentage);
      });
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [fillPercentage]);

  // Generate tick marks based on syringe size - like a ruler
  const ticks = useMemo(() => {
    const tickArray = [];
    const step = totalUnits <= 30 ? 1 : totalUnits <= 50 ? 2 : 2;
    const majorStep = 10;
    
    for (let i = 0; i <= totalUnits; i += step) {
      const isMajor = i % majorStep === 0;
      const isMedium = i % 5 === 0 && !isMajor;
      tickArray.push({
        value: i,
        position: (i / totalUnits) * 100,
        isMajor,
        isMedium,
        showLabel: isMajor
      });
    }
    return tickArray;
  }, [totalUnits]);

  return (
    <div className="w-full mt-6">
      {/* Syringe ruler container */}
      <div className="relative">
        {/* Main syringe body - ruler style with cream/white background */}
        <div className="relative bg-gradient-to-b from-slate-100 to-slate-200 rounded-lg border border-slate-300 overflow-hidden shadow-md">
          
          {/* Fill level - sits at the bottom like liquid */}
          <div className="relative h-16">
            {/* Filled portion - starts from left */}
            <div 
              className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-[#4d82a9]/80 to-[#6a9dc4]/60 transition-all duration-500 ease-out"
              style={{ width: `${Math.min(animatedFill, 100)}%` }}
            />
            
            {/* Tick marks - vertical lines from top */}
            <div className="absolute inset-x-0 top-0 h-6 flex">
              {ticks.map((tick, index) => (
                <div
                  key={index}
                  className="absolute flex flex-col items-center"
                  style={{ 
                    left: `${tick.position}%`,
                    transform: 'translateX(-50%)'
                  }}
                >
                  <div 
                    className={`
                      bg-slate-700
                      ${tick.isMajor ? 'h-5 w-[1.5px]' : tick.isMedium ? 'h-3 w-[1px]' : 'h-2 w-[0.5px]'}
                    `}
                    style={{ opacity: tick.isMajor ? 1 : tick.isMedium ? 0.7 : 0.4 }}
                  />
                </div>
              ))}
            </div>
          </div>
          
          {/* Labels row - positioned inside at bottom of ruler area */}
          <div className="relative h-6 bg-gradient-to-b from-slate-200 to-slate-100 border-t border-slate-300">
            {ticks.filter(t => t.showLabel).map((tick, index) => (
              <span
                key={index}
                className="absolute text-[11px] font-semibold text-slate-700 top-1"
                style={{ 
                  left: `${tick.position}%`,
                  transform: tick.position === 100 ? 'translateX(-100%)' : tick.position === 0 ? 'translateX(10%)' : 'translateX(-50%)'
                }}
              >
                {tick.value}
              </span>
            ))}
          </div>
        </div>
        
        {/* Fill indicator - triangle pointer at top */}
        {fillPercentage > 0 && fillPercentage <= 100 && (
          <div 
            className="absolute -top-3 transition-all duration-500 ease-out z-10"
            style={{ left: `${Math.min(animatedFill, 100)}%`, transform: 'translateX(-50%)' }}
          >
            <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-[#4d82a9]" />
          </div>
        )}
      </div>
    </div>
  );
};

export default SyringeVisualization;
